/**
 * Shared confirm/notify primitives so admin flows don't fall back to
 * `window.confirm` / `window.alert` (which look like a 90s intranet app and
 * can't carry rich content).
 *
 * Two pieces:
 *   - `<ConfirmDialog />` — declarative, controlled Yes/No modal.
 *   - `useConfirm()` / `useNotify()` — hook-flavoured imperative API for
 *     callsites where inline state is overkill.
 */
import React, {createContext, useCallback, useContext, useMemo, useState} from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';

export interface ConfirmDialogProps {
  open: boolean;
  title?: string;
  message: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  /**
   * Optional safeguard for irreversible destructive actions. When
   * set, the confirm button stays disabled until the user types
   * this exact string. Use for purges/deletes where the cost of
   * an accidental click is high.
   */
  requireText?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog(props: ConfirmDialogProps) {
  const [typed, setTyped] = useState('');
  // Reset the typed value whenever the dialog opens so the gate
  // resets between separate confirms (otherwise the previous
  // confirmation's text leaks through to the next one).
  React.useEffect(() => {
    if (props.open) setTyped('');
  }, [props.open]);

  const requireMet = !props.requireText || typed === props.requireText;
  return (
    <Dialog open={props.open} onClose={props.onCancel} maxWidth="xs" fullWidth>
      {props.title && <DialogTitle sx={{fontSize: 16, fontWeight: 600}}>{props.title}</DialogTitle>}
      <DialogContent>
        <DialogContentText sx={{fontSize: 13, whiteSpace: 'pre-line'}}>
          {props.message}
        </DialogContentText>
        {props.requireText && (
          <Box sx={{mt: 2}}>
            <Typography sx={{fontSize: 12, mb: 0.5, color: 'text.secondary'}}>
              Type <code>{props.requireText}</code> to confirm:
            </Typography>
            <TextField
              autoFocus
              size="small"
              fullWidth
              value={typed}
              onChange={e => setTyped(e.target.value)}
              placeholder={props.requireText}
              inputProps={{style: {fontFamily: 'monospace', fontSize: 12}}}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{px: 3, pb: 2}}>
        <Button size="small" onClick={props.onCancel}>
          {props.cancelLabel || 'Cancel'}
        </Button>
        <Button
          size="small"
          variant="contained"
          color={props.destructive ? 'error' : 'primary'}
          onClick={props.onConfirm}
          disabled={!requireMet}
          autoFocus={!props.requireText}
        >
          {props.confirmLabel || 'Confirm'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// -----------------------------------------------------------------------------
// Hook-flavoured API — wraps a single confirm dialog instance + notify snackbar
// in a provider so any descendant can just `await confirm({...})`.
// -----------------------------------------------------------------------------

interface ConfirmOptions {
  title?: string;
  message: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  requireText?: string;
}

interface NotifyOptions {
  severity?: 'success' | 'info' | 'warning' | 'error';
  message: React.ReactNode;
  durationMs?: number;
  /**
   * Optional call-to-action rendered on the right side of the snackbar.
   * Primary use: linking to a freshly-queued background job's row on
   * /admin/operations/tasks so the admin can watch progress in one click.
   */
  action?: {label: string; href: string};
}

interface DialogContextShape {
  confirm: (opts: ConfirmOptions) => Promise<boolean>;
  notify: (opts: NotifyOptions) => void;
}

const DialogContext = createContext<DialogContextShape | null>(null);

export function DialogProvider({children}: {children: React.ReactNode}) {
  const [confirmState, setConfirmState] = useState<
    (ConfirmOptions & {resolve: (ok: boolean) => void}) | null
  >(null);
  const [notifyState, setNotifyState] = useState<NotifyOptions | null>(null);

  const confirm = useCallback(
    (opts: ConfirmOptions) =>
      new Promise<boolean>(resolve => {
        setConfirmState({...opts, resolve});
      }),
    [],
  );
  const notify = useCallback((opts: NotifyOptions) => {
    setNotifyState(opts);
  }, []);

  const value = useMemo<DialogContextShape>(() => ({confirm, notify}), [confirm, notify]);

  const closeConfirm = (result: boolean) => {
    confirmState?.resolve(result);
    setConfirmState(null);
  };

  return (
    <DialogContext.Provider value={value}>
      {children}
      <ConfirmDialog
        open={!!confirmState}
        title={confirmState?.title}
        message={confirmState?.message ?? ''}
        confirmLabel={confirmState?.confirmLabel}
        cancelLabel={confirmState?.cancelLabel}
        destructive={confirmState?.destructive}
        requireText={confirmState?.requireText}
        onConfirm={() => closeConfirm(true)}
        onCancel={() => closeConfirm(false)}
      />
      <Snackbar
        open={!!notifyState}
        autoHideDuration={notifyState?.durationMs ?? 4000}
        onClose={() => setNotifyState(null)}
        anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
      >
        {notifyState ? (
          <Alert
            severity={notifyState.severity ?? 'info'}
            onClose={() => setNotifyState(null)}
            variant="filled"
            sx={{fontSize: 13, alignItems: 'center'}}
            action={
              notifyState.action ? (
                <Button
                  size="small"
                  color="inherit"
                  href={notifyState.action.href}
                  // Dismiss the snackbar on click so the destination page
                  // isn't covered by a stale toast from the previous view.
                  onClick={() => setNotifyState(null)}
                  sx={{textTransform: 'none', fontWeight: 700}}
                >
                  {notifyState.action.label}
                </Button>
              ) : undefined
            }
          >
            {notifyState.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </DialogContext.Provider>
  );
}

export function useConfirm(): DialogContextShape['confirm'] {
  const ctx = useContext(DialogContext);
  if (!ctx) throw new Error('useConfirm must be used inside <DialogProvider>');
  return ctx.confirm;
}

export function useNotify(): DialogContextShape['notify'] {
  const ctx = useContext(DialogContext);
  if (!ctx) throw new Error('useNotify must be used inside <DialogProvider>');
  return ctx.notify;
}
